import { Textarea as MTTextarea } from '@material-tailwind/react';
import * as React from 'react';

import { cn } from '@/shared/utils/cn';

export type LabelTextareaComponentProps = Omit<
  React.ComponentProps<typeof MTTextarea>,
  | 'crossOrigin'
  | 'onPointerEnterCapture'
  | 'onPointerLeaveCapture'
  | 'onResize'
  | 'onResizeCapture'
>;

export interface LabelTextareaProps extends LabelTextareaComponentProps {
  label: string;
  crossOrigin?: string;
  onPointerEnterCapture?: () => void;
  onPointerLeaveCapture?: () => void;
  onResize?: () => void;
  onResizeCapture?: () => void;
}

const LabelTextarea = React.forwardRef<HTMLDivElement, LabelTextareaProps>(
  ({ className, label, resize, ...props }, ref) => {
    const textareaProps = {
      ...props,
      className: cn(
        'placeholder:text-custom-light-grey placeholder:opacity-100 placeholder:text-sm resize-y',
        className,
      ),
      label,
      labelProps: {
        className: props.error
          ? 'before:!border-error-red after:!border-error-red'
          : '',
      },
      crossOrigin: props.crossOrigin || '',
      onPointerEnterCapture: props.onPointerEnterCapture || (() => {}),
      onPointerLeaveCapture: props.onPointerLeaveCapture || (() => {}),
      onResize: props.onResize || (() => {}),
      onResizeCapture: props.onResizeCapture || (() => {}),
    };

    return (
      <MTTextarea
        ref={ref}
        variant="outlined"
        resize={resize}
        {...textareaProps}
      />
    );
  },
);

LabelTextarea.displayName = 'Textarea';

export { LabelTextarea };

export const TextareaTheme = {
  defaultProps: {
    color: 'white',
    shrink: true,
  },
  styles: {
    base: {
      textarea: {
        color: 'text-white',
        disabled: 'disabled:bg-transparent disabled:cursor-not-allowed',
      },
      label: {
        color: 'text-white',
      },
    },
    variants: {
      outlined: {
        base: {
          textarea: {
            borderWidth: 'border-[0.5px] !focus:border-[0.5px]',
            floated: {
              borderWidth: 'border-[0.5px]',
            },
          },
          label: {
            before: {
              floated: {
                bt: 'before:border-t-[0.5px] peer-focus:before:border-t-[0.5px]',
                bl: 'before:border-l-[0.5px] peer-focus:before:border-l-[0.5px]',
              },
            },
            after: {
              floated: {
                bt: 'after:border-t-[0.5px] peer-focus:after:border-t-[0.5px]',
                br: 'after:border-r-[0.5px] peer-focus:after:border-r-[0.5px]',
              },
            },
          },
        },
        sizes: {
          md: {
            textarea: {
              fontSize: 'text-sm',
            },
          },
        },
        colors: {
          textarea: {
            white: {
              borderColor: 'border-white/70',
              borderColorFocused: 'focus:border-white/70',
            },
          },
          label: {
            white: {
              before:
                'before:border-tl-white/70 peer-focus:before:!border-tl-white/70',
              after:
                'after:border-tr-white/70 peer-focus:after:!border-tr-white/70',
            },
          },
        },
        shrink: {
          label: {
            fontSize: '!text-[12px]',
          },
        },
      },
    },
  },
};
